import React, { useState, useRef, useEffect, useMemo, useCallback } from "react";
import {
  Box, Typography, TextField, IconButton, Avatar, Chip, Button,
  alpha, useTheme, CircularProgress, InputAdornment, Badge, Fab,
  Skeleton, Checkbox, Divider, Tooltip, useMediaQuery,
} from "@mui/material";
import { useSelector } from "react-redux";
import { selectEnableAuth } from "../../Store/Auth/AuthSelector";
import {
  useGetChatConversationsQuery, useGetChatMessagesQuery, useSendChatMessageMutation,
  useGetChatContactsQuery, useCreateChatConversationMutation, useGetChatUnreadCountQuery,
  useMarkChatAsReadMutation,
} from "../../Api/attoDeskApi";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import calendar from "dayjs/plugin/calendar";

// Icons
import ChatIcon from "@mui/icons-material/Chat";
import CloseIcon from "@mui/icons-material/Close";
import SendIcon from "@mui/icons-material/Send";
import SearchIcon from "@mui/icons-material/Search";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AddCommentOutlinedIcon from "@mui/icons-material/AddCommentOutlined";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import GroupsIcon from "@mui/icons-material/Groups";
import OpenInFullIcon from "@mui/icons-material/OpenInFull";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import { useNavigate, useLocation } from "react-router-dom";

dayjs.extend(relativeTime);
dayjs.extend(calendar);

// ════════════════════════════════════════════════════════════════
// Constants
// ════════════════════════════════════════════════════════════════

const W = 368;
const H_MINI = 560;
const H_EXPANDED = 680;
const W_EXPANDED = 440;
const RADIUS = 18;

type View = "list" | "thread" | "new";
type Filter = "all" | "unread" | "parents" | "staff";

// ════════════════════════════════════════════════════════════════
// Root Widget
// ════════════════════════════════════════════════════════════════

const ChatWidget = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const isAuth = useSelector(selectEnableAuth);
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [open, setOpen] = useState(false);
  const [view, setView] = useState<View>("list");
  const [activeConvId, setActiveConvId] = useState<number | null>(null);

  const { data: unreadData } = useGetChatUnreadCountQuery(undefined, { pollingInterval: 12000, skip: !isAuth });
  const unreadCount = (unreadData?.data as any)?.unread_count || 0;

  // Hide on chat/messages pages and when not authenticated
  const hiddenRoutes = ["/chat", "/messages", "/message/create"];
  const isHidden = !isAuth || hiddenRoutes.some((r) => location.pathname.startsWith(r));

  if (isHidden) return null;

  const openThread = (id: number) => { setActiveConvId(id); setView("thread"); };
  const backToList = () => { setActiveConvId(null); setView("list"); };
  const goFullPage = () => { setOpen(false); setView("list"); setActiveConvId(null); navigate("/chat"); };

  const panelW = isMobile ? "100vw" : W;
  const panelH = isMobile ? "100vh" : H_MINI;

  return (
    <>
      {/* ── Floating Launcher ── */}
      <Fab
        onClick={() => setOpen(!open)}
        sx={{
          // Below MUI's modal/drawer layer (1300) so open drawers & dialogs are
          // never covered by the chat launcher; still above normal page content.
          position: "fixed", bottom: 24, right: 24, zIndex: 1250,
          width: isMobile ? 52 : 56, height: isMobile ? 52 : 56,
          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${alpha(theme.palette.primary.main, 0.75)} 100%)`,
          color: "#fff",
          boxShadow: "0 4px 16px rgba(0,0,0,0.22)",
          "&:hover": { transform: "scale(1.05)", boxShadow: "0 6px 22px rgba(0,0,0,0.28)" },
          "&:active": { transform: "scale(0.95)" },
          transition: "all 0.2s cubic-bezier(0.4,0,0.2,1)",
        }}
      >
        <Badge
          badgeContent={unreadCount > 99 ? "99+" : unreadCount}
          color="error"
          invisible={unreadCount === 0 || open}
          sx={{
            "& .MuiBadge-badge": {
              fontSize: 9, minWidth: 18, height: 18, top: -6, right: -6,
              animation: unreadCount > 0 ? "popIn 0.3s ease" : "none",
              "@keyframes popIn": { "0%": { transform: "scale(0)" }, "100%": { transform: "scale(1)" } },
            },
          }}
        >
          {open ? <CloseIcon /> : <ChatIcon />}
        </Badge>
      </Fab>

      {/* ── Panel ── */}
      <Box
        sx={{
          position: "fixed",
          bottom: isMobile ? 0 : 88,
          right: isMobile ? 0 : 24,
          width: panelW,
          height: panelH,
          bgcolor: "#fff",
          borderRadius: isMobile ? 0 : `${RADIUS}px`,
          boxShadow: "0 8px 40px rgba(0,0,0,0.16), 0 2px 8px rgba(0,0,0,0.08)",
          border: isMobile ? "none" : "1px solid rgba(0,0,0,0.06)",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          zIndex: 1499,
          opacity: open ? 1 : 0,
          transform: open ? "translateY(0) scale(1)" : "translateY(12px) scale(0.97)",
          pointerEvents: open ? "auto" : "none",
          transition: "opacity 0.22s cubic-bezier(0.4,0,0.2,1), transform 0.22s cubic-bezier(0.4,0,0.2,1)",
        }}
      >
        {/* The panel is always in the DOM (opacity/transform-based transition),
            but mounting the inner views when the panel is closed burns a
            6-second polling conversation fetch on every page the launcher is
            visible. Mount only when the user has opened the panel; the
            launcher badge still updates from `useGetChatUnreadCountQuery`
            above, which is cheap and intentionally kept live. */}
        {open && view === "list" && (
          <ConversationList
            onSelect={openThread}
            onNewChat={() => setView("new")}
            onClose={() => setOpen(false)}
            onExpand={goFullPage}
            expanded={false}
            onFullPage={goFullPage}
          />
        )}
        {open && view === "thread" && activeConvId && (
          <ChatThread conversationId={activeConvId} onBack={backToList} />
        )}
        {open && view === "new" && (
          <NewChatPanel onBack={backToList} onCreated={(id) => openThread(id)} />
        )}
      </Box>
    </>
  );
};

// ════════════════════════════════════════════════════════════════
// Conversation List
// ════════════════════════════════════════════════════════════════

const ConversationList = ({
  onSelect, onNewChat, onClose, onExpand, expanded, onFullPage,
}: {
  onSelect: (id: number) => void; onNewChat: () => void; onClose: () => void;
  onExpand: () => void; expanded: boolean; onFullPage: () => void;
}) => {
  const theme = useTheme();
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState<Filter>("all");

  const { data: convData, isLoading } = useGetChatConversationsQuery(undefined, { pollingInterval: 6000 });
  const conversations: any[] = useMemo(() => Array.isArray(convData?.data) ? convData!.data as any[] : [], [convData]);
  const totalUnread = conversations.reduce((s: number, c: any) => s + (c.unread_count || 0), 0);

  const filtered = useMemo(() => {
    let list = conversations;
    if (search) {
      const q = search.toLowerCase();
      list = list.filter((c: any) => (c.title || "").toLowerCase().includes(q));
    }
    if (filter === "unread") list = list.filter((c: any) => (c.unread_count || 0) > 0);
    if (filter === "parents") list = list.filter((c: any) => c.participants?.some((p: any) => p.user_type === "parent"));
    if (filter === "staff") list = list.filter((c: any) => c.participants?.every((p: any) => p.user_type === "employee"));
    return list;
  }, [conversations, search, filter]);

  const filters: { key: Filter; label: string }[] = [
    { key: "all", label: "All" },
    { key: "unread", label: `Unread${totalUnread > 0 ? ` (${totalUnread})` : ""}` },
    { key: "parents", label: "Parents" },
    { key: "staff", label: "Staff" },
  ];

  return (
    <>
      {/* Header */}
      <Box sx={{
        px: 2, py: 1.5,
        background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${alpha(theme.palette.primary.main, 0.82)})`,
        color: "#fff", display: "flex", alignItems: "center", justifyContent: "space-between",
      }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography sx={{ fontSize: 16, fontWeight: 700 }}>Messages</Typography>
          {totalUnread > 0 && (
            <Chip label={totalUnread} size="small" sx={{ height: 20, fontSize: 10, fontWeight: 700, bgcolor: "rgba(255,255,255,0.2)", color: "#fff" }} />
          )}
        </Box>
        <Box sx={{ display: "flex", gap: 0.25 }}>
          <Tooltip title="New chat"><IconButton size="small" onClick={onNewChat} sx={{ color: "#fff" }}><AddCommentOutlinedIcon sx={{ fontSize: 18 }} /></IconButton></Tooltip>
          <Tooltip title="Open full page"><IconButton size="small" onClick={onFullPage} sx={{ color: "#fff" }}><OpenInFullIcon sx={{ fontSize: 16 }} /></IconButton></Tooltip>
          <IconButton size="small" onClick={onClose} sx={{ color: "#fff" }}><CloseIcon sx={{ fontSize: 18 }} /></IconButton>
        </Box>
      </Box>

      {/* Search */}
      <Box sx={{ px: 1.5, pt: 1.25, pb: 0.5 }}>
        <TextField size="small" fullWidth placeholder="Search conversations..." value={search} onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 16, color: "text.disabled" }} /></InputAdornment>,
            sx: { fontSize: 12.5, borderRadius: "12px", bgcolor: "rgba(0,0,0,0.025)", height: 36 },
          }}
        />
      </Box>

      {/* Filters */}
      <Box sx={{ px: 1.5, py: 0.75, display: "flex", gap: 0.5, overflowX: "auto", "&::-webkit-scrollbar": { display: "none" } }}>
        {filters.map((f) => (
          <Chip key={f.key} label={f.label} size="small" onClick={() => setFilter(f.key)}
            sx={{
              height: 26, fontSize: 11, fontWeight: filter === f.key ? 700 : 500, flexShrink: 0,
              bgcolor: filter === f.key ? alpha(theme.palette.primary.main, 0.1) : "rgba(0,0,0,0.03)",
              color: filter === f.key ? theme.palette.primary.main : "text.secondary",
              border: filter === f.key ? `1px solid ${alpha(theme.palette.primary.main, 0.2)}` : "1px solid transparent",
              cursor: "pointer", "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.06) },
            }}
          />
        ))}
      </Box>

      <Divider sx={{ mx: 1.5 }} />

      {/* List */}
      <Box sx={{ flex: 1, overflowY: "auto" }}>
        {isLoading ? (
          <Box sx={{ px: 1.5 }}>
            {[1, 2, 3, 4, 5].map((i) => (
              <Box key={i} sx={{ display: "flex", gap: 1.25, py: 1.25, px: 0.5 }}>
                <Skeleton variant="circular" width={42} height={42} />
                <Box sx={{ flex: 1 }}>
                  <Skeleton width="60%" height={16} />
                  <Skeleton width="80%" height={14} sx={{ mt: 0.5 }} />
                </Box>
              </Box>
            ))}
          </Box>
        ) : filtered.length === 0 ? (
          <Box sx={{ textAlign: "center", py: 5, px: 2 }}>
            <ChatBubbleOutlineIcon sx={{ fontSize: 36, color: "rgba(0,0,0,0.08)", mb: 1 }} />
            <Typography sx={{ fontSize: 13, fontWeight: 600, mb: 0.5 }}>No conversations</Typography>
            <Typography sx={{ fontSize: 11.5, color: "text.secondary", mb: 2 }}>Start a chat with a colleague or parent</Typography>
            <Button size="small" variant="contained" onClick={onNewChat}
              sx={{ textTransform: "none", fontSize: 11.5, boxShadow: "none", borderRadius: "10px" }}>
              New Conversation
            </Button>
          </Box>
        ) : (
          filtered.map((conv: any) => {
            const hasUnread = (conv.unread_count || 0) > 0;
            return (
              <Box key={conv.id} onClick={() => onSelect(conv.id)}
                sx={{
                  display: "flex", alignItems: "center", gap: 1.25,
                  px: 2, py: 1.35, cursor: "pointer",
                  bgcolor: hasUnread ? alpha(theme.palette.primary.main, 0.02) : "transparent",
                  "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.04) },
                  borderBottom: "1px solid rgba(0,0,0,0.04)",
                  borderLeft: hasUnread ? `3px solid ${theme.palette.primary.main}` : "3px solid transparent",
                  transition: "all 0.12s ease",
                }}>
                <Badge
                  variant={hasUnread ? "dot" : "standard"}
                  color="primary"
                  invisible={!hasUnread}
                  overlap="circular"
                  anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                  sx={{ "& .MuiBadge-dot": { width: 10, height: 10, borderRadius: "50%", border: "2px solid #fff" } }}
                >
                  <Avatar sx={{
                    width: 44, height: 44, fontSize: 15, fontWeight: 700,
                    bgcolor: alpha(theme.palette.primary.main, 0.07),
                    color: theme.palette.primary.main,
                  }}>
                    {conv.type === "group" ? <GroupsIcon sx={{ fontSize: 20 }} /> : (conv.title || "C").charAt(0).toUpperCase()}
                  </Avatar>
                </Badge>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
                    <Typography sx={{
                      fontSize: 13.5, fontWeight: hasUnread ? 700 : 500,
                      color: hasUnread ? "text.primary" : "text.primary",
                      overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                      letterSpacing: "-0.01em",
                    }}>
                      {conv.title}
                    </Typography>
                    {conv.latest_message && (
                      <Typography sx={{
                        fontSize: 10, flexShrink: 0, ml: 1,
                        color: hasUnread ? theme.palette.primary.main : "text.disabled",
                        fontWeight: hasUnread ? 700 : 400,
                      }}>
                        {dayjs(conv.latest_message.created_at).fromNow(true)}
                      </Typography>
                    )}
                  </Box>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mt: 0.2 }}>
                    <Typography sx={{
                      flex: 1, fontSize: 12, lineHeight: 1.3,
                      color: hasUnread ? "text.secondary" : "text.disabled",
                      fontWeight: hasUnread ? 500 : 400,
                      overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                    }}>
                      {conv.latest_message?.body || "No messages yet"}
                    </Typography>
                    {hasUnread && (
                      <Chip label={conv.unread_count} size="small" sx={{
                        height: 18, minWidth: 18, fontSize: 10, fontWeight: 700,
                        bgcolor: theme.palette.primary.main, color: "#fff",
                        "& .MuiChip-label": { px: 0.5 },
                      }} />
                    )}
                  </Box>
                  {conv.student_name && (
                    <Chip label={conv.student_name} size="small"
                      sx={{ height: 16, fontSize: 9, mt: 0.35, bgcolor: alpha("#6366f1", 0.06), color: "#6366f1", fontWeight: 600, "& .MuiChip-label": { px: 0.5 } }} />
                  )}
                </Box>
              </Box>
            );
          })
        )}
      </Box>
    </>
  );
};

// ════════════════════════════════════════════════════════════════
// Chat Thread
// ════════════════════════════════════════════════════════════════

const ChatThread = ({ conversationId, onBack }: { conversationId: number; onBack: () => void }) => {
  const theme = useTheme();
  const [text, setText] = useState("");
  const endRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const [showNewPill, setShowNewPill] = useState(false);

  const { data: convData } = useGetChatConversationsQuery();
  const conv = useMemo(() => {
    const list = Array.isArray(convData?.data) ? convData!.data as any[] : [];
    return list.find((c: any) => c.id === conversationId) || null;
  }, [convData, conversationId]);

  const { data: msgData, isLoading } = useGetChatMessagesQuery({ conversation_id: conversationId }, { pollingInterval: 4000 });
  const [sendMsg, { isLoading: sending }] = useSendChatMessageMutation();
  const [markRead] = useMarkChatAsReadMutation();

  const messages: any[] = useMemo(() => Array.isArray(msgData?.data) ? msgData!.data as any[] : [], [msgData]);

  // Mark as read on open
  useEffect(() => { markRead({ conversation_id: conversationId }); }, [conversationId]);

  // Auto-scroll
  const isNearBottom = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return true;
    return el.scrollHeight - el.scrollTop - el.clientHeight < 100;
  }, []);

  useEffect(() => {
    if (isNearBottom()) {
      endRef.current?.scrollIntoView({ behavior: "smooth" });
    } else {
      setShowNewPill(true);
    }
  }, [messages.length]);

  // Initial scroll
  useEffect(() => {
    setTimeout(() => endRef.current?.scrollIntoView({ behavior: "auto" }), 100);
  }, [conversationId]);

  const handleSend = async () => {
    const body = text.trim();
    if (!body || sending) return;
    setText("");
    try {
      await sendMsg({ conversation_id: conversationId, body }).unwrap();
      setTimeout(() => endRef.current?.scrollIntoView({ behavior: "smooth" }), 50);
    } catch {}
  };

  // Group messages by day
  const groupedMessages = useMemo(() => {
    const groups: { date: string; messages: any[] }[] = [];
    let lastDate = "";
    messages.forEach((msg: any) => {
      const date = dayjs(msg.created_at).format("YYYY-MM-DD");
      if (date !== lastDate) {
        groups.push({ date, messages: [msg] });
        lastDate = date;
      } else {
        groups[groups.length - 1].messages.push(msg);
      }
    });
    return groups;
  }, [messages]);

  const formatDayLabel = (dateStr: string) => {
    const d = dayjs(dateStr);
    if (d.isSame(dayjs(), "day")) return "Today";
    if (d.isSame(dayjs().subtract(1, "day"), "day")) return "Yesterday";
    return d.format("MMMM D, YYYY");
  };

  return (
    <>
      {/* Header */}
      <Box sx={{
        px: 1.5, py: 1.25,
        background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${alpha(theme.palette.primary.main, 0.82)})`,
        color: "#fff", display: "flex", alignItems: "center", gap: 1,
      }}>
        <IconButton size="small" onClick={onBack} sx={{ color: "#fff" }}><ArrowBackIcon sx={{ fontSize: 18 }} /></IconButton>
        <Avatar sx={{ width: 34, height: 34, fontSize: 13, fontWeight: 700, bgcolor: "rgba(255,255,255,0.18)" }}>
          {(conv?.title || "C").charAt(0).toUpperCase()}
        </Avatar>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: 14, fontWeight: 700, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {conv?.title || "Conversation"}
          </Typography>
          <Typography sx={{ fontSize: 10.5, opacity: 0.75 }}>
            {conv?.participants?.length || 0} participants
          </Typography>
        </Box>
        <IconButton size="small" onClick={onBack} sx={{ color: "#fff" }}><CloseIcon sx={{ fontSize: 16 }} /></IconButton>
      </Box>

      {/* Messages */}
      <Box ref={scrollRef} sx={{
        flex: 1, overflowY: "auto", px: 1.5, py: 1,
        display: "flex", flexDirection: "column",
        bgcolor: "rgba(0,0,0,0.012)",
        position: "relative",
      }}>
        {isLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}><CircularProgress size={22} /></Box>
        ) : messages.length === 0 ? (
          <Box sx={{ textAlign: "center", py: 4 }}>
            <ChatBubbleOutlineIcon sx={{ fontSize: 28, color: "rgba(0,0,0,0.08)", mb: 1 }} />
            <Typography sx={{ fontSize: 12.5, color: "text.secondary" }}>Say hello!</Typography>
          </Box>
        ) : (
          groupedMessages.map((group) => (
            <React.Fragment key={group.date}>
              {/* Day divider */}
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, py: 1.5 }}>
                <Box sx={{ flex: 1, height: "1px", bgcolor: "rgba(0,0,0,0.06)" }} />
                <Typography sx={{ fontSize: 10, fontWeight: 600, color: "text.disabled", textTransform: "uppercase", letterSpacing: "0.05em", flexShrink: 0 }}>
                  {formatDayLabel(group.date)}
                </Typography>
                <Box sx={{ flex: 1, height: "1px", bgcolor: "rgba(0,0,0,0.06)" }} />
              </Box>

              {group.messages.map((msg: any, i: number) => {
                const isMine = msg.is_mine;
                const showSender = !isMine && (i === 0 || group.messages[i - 1]?.sender_id !== msg.sender_id);
                return (
                  <Box key={msg.id} sx={{
                    display: "flex", flexDirection: "column",
                    alignItems: isMine ? "flex-end" : "flex-start",
                    mt: showSender ? 1 : 0.2,
                    // Subtle entrance animation
                    animation: "msgIn 0.15s ease-out",
                    "@keyframes msgIn": {
                      from: { opacity: 0, transform: isMine ? "scale(0.98)" : "translateY(4px)" },
                      to: { opacity: 1, transform: "none" },
                    },
                  }}>
                    {showSender && (
                      <Typography sx={{ fontSize: 10, fontWeight: 600, color: "text.secondary", mb: 0.2, ml: 0.5 }}>
                        {msg.sender_name}
                      </Typography>
                    )}
                    <Box sx={{
                      maxWidth: "72%", px: 1.5, py: 0.85,
                      borderRadius: isMine ? "16px 16px 4px 16px" : "16px 16px 16px 4px",
                      bgcolor: isMine ? theme.palette.primary.main : "#f0f1f4",
                      color: isMine ? "#fff" : "text.primary",
                      boxShadow: isMine ? "0 1px 3px rgba(0,0,0,0.1)" : "none",
                    }}>
                      {msg.reply_to && (
                        <Box sx={{
                          px: 1, py: 0.4, mb: 0.5, borderRadius: "8px",
                          bgcolor: isMine ? "rgba(255,255,255,0.12)" : "rgba(0,0,0,0.04)",
                          borderLeft: `2px solid ${isMine ? "rgba(255,255,255,0.35)" : theme.palette.primary.main}`,
                        }}>
                          <Typography sx={{ fontSize: 9.5, fontWeight: 600, opacity: 0.65 }}>{msg.reply_to.sender_name}</Typography>
                          <Typography sx={{ fontSize: 10, opacity: 0.75 }}>{msg.reply_to.body}</Typography>
                        </Box>
                      )}
                      <Typography sx={{ fontSize: 13, lineHeight: 1.45, wordBreak: "break-word", whiteSpace: "pre-wrap" }}>
                        {msg.body}
                      </Typography>
                      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 0.3, mt: 0.2 }}>
                        <Typography sx={{ fontSize: 9, opacity: 0.5 }}>
                          {dayjs(msg.created_at).format("h:mm A")}
                        </Typography>
                        {isMine && <DoneAllIcon sx={{ fontSize: 12, opacity: 0.5 }} />}
                      </Box>
                    </Box>
                  </Box>
                );
              })}
            </React.Fragment>
          ))
        )}

        {/* New messages pill */}
        {showNewPill && (
          <Box
            onClick={() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); setShowNewPill(false); }}
            sx={{
              position: "sticky", bottom: 8, alignSelf: "center",
              px: 2, py: 0.5, borderRadius: "20px",
              bgcolor: theme.palette.primary.main, color: "#fff",
              fontSize: 11, fontWeight: 600, cursor: "pointer",
              boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
            }}
          >
            New messages ↓
          </Box>
        )}
        <div ref={endRef} />
      </Box>

      {/* Composer */}
      <Box sx={{
        px: 1.25, py: 1, borderTop: "1px solid rgba(0,0,0,0.06)",
        display: "flex", gap: 0.75, alignItems: "flex-end",
        bgcolor: "#fff",
      }}>
        <TextField fullWidth multiline maxRows={3} size="small" placeholder="Type a message..."
          value={text} onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
          InputProps={{ sx: { fontSize: 13, borderRadius: "14px", bgcolor: "rgba(0,0,0,0.02)", py: 0.25 } }}
        />
        <IconButton onClick={handleSend} disabled={!text.trim() || sending}
          sx={{
            background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${alpha(theme.palette.primary.main, 0.8)})`,
            color: "#fff", width: 38, height: 38, flexShrink: 0,
            "&:hover": { background: theme.palette.primary.dark },
            "&:disabled": { background: "rgba(0,0,0,0.06)", color: "text.disabled" },
            transition: "all 0.15s",
          }}>
          {sending ? <CircularProgress size={16} color="inherit" /> : <SendIcon sx={{ fontSize: 18 }} />}
        </IconButton>
      </Box>
    </>
  );
};

// ════════════════════════════════════════════════════════════════
// New Chat Panel
// ════════════════════════════════════════════════════════════════

const NewChatPanel = ({ onBack, onCreated }: { onBack: () => void; onCreated: (id: number) => void }) => {
  const theme = useTheme();
  const [selected, setSelected] = useState<number[]>([]);
  const [search, setSearch] = useState("");

  const { data: contactData, isLoading } = useGetChatContactsQuery();
  const [createConv, { isLoading: creating }] = useCreateChatConversationMutation();

  const contacts: any[] = useMemo(() => Array.isArray(contactData?.data) ? contactData!.data as any[] : [], [contactData]);
  const filtered = useMemo(() => {
    if (!search) return contacts;
    const q = search.toLowerCase();
    return contacts.filter((c: any) => (c.name || "").toLowerCase().includes(q));
  }, [contacts, search]);

  const handleCreate = async () => {
    if (selected.length === 0) return;
    try {
      const r = await createConv({ type: selected.length === 1 ? "direct" : "group", participant_ids: selected }).unwrap();
      if (r.status && r.data) onCreated((r.data as any).id);
    } catch {}
  };

  return (
    <>
      <Box sx={{
        px: 1.5, py: 1.25,
        background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${alpha(theme.palette.primary.main, 0.82)})`,
        color: "#fff", display: "flex", alignItems: "center", gap: 1,
      }}>
        <IconButton size="small" onClick={onBack} sx={{ color: "#fff" }}><ArrowBackIcon sx={{ fontSize: 18 }} /></IconButton>
        <Typography sx={{ fontSize: 15, fontWeight: 700 }}>New Conversation</Typography>
      </Box>

      <Box sx={{ px: 1.5, py: 1 }}>
        <TextField size="small" fullWidth placeholder="Search contacts..." value={search} onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 15, color: "text.disabled" }} /></InputAdornment>,
            sx: { fontSize: 12.5, borderRadius: "12px", height: 36 },
          }}
        />
      </Box>

      <Box sx={{ flex: 1, overflowY: "auto" }}>
        {isLoading ? (
          <Box sx={{ px: 1.5 }}>
            {[1, 2, 3, 4].map((i) => (
              <Box key={i} sx={{ display: "flex", gap: 1, py: 1 }}>
                <Skeleton variant="circular" width={36} height={36} />
                <Box sx={{ flex: 1 }}><Skeleton width="50%" height={14} /><Skeleton width="30%" height={12} sx={{ mt: 0.5 }} /></Box>
              </Box>
            ))}
          </Box>
        ) : filtered.length === 0 ? (
          <Typography sx={{ textAlign: "center", py: 4, fontSize: 12.5, color: "text.secondary" }}>No contacts available</Typography>
        ) : (
          filtered.map((c: any) => {
            const isSelected = selected.includes(c.id);
            const isParent = c.user_type === "parent";
            return (
              <Box key={c.id}
                onClick={() => setSelected(prev => prev.includes(c.id) ? prev.filter(x => x !== c.id) : [...prev, c.id])}
                sx={{
                  display: "flex", alignItems: "center", gap: 1.25, px: 2, py: 1, cursor: "pointer",
                  bgcolor: isSelected ? alpha(theme.palette.primary.main, 0.04) : "transparent",
                  "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.06) },
                  borderBottom: "1px solid rgba(0,0,0,0.03)",
                }}>
                <Avatar sx={{
                  width: 36, height: 36, fontSize: 13, fontWeight: 700,
                  bgcolor: alpha(isParent ? "#f59e0b" : theme.palette.primary.main, 0.08),
                  color: isParent ? "#f59e0b" : theme.palette.primary.main,
                }}>
                  {(c.name || "U").charAt(0).toUpperCase()}
                </Avatar>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{ fontSize: 13, fontWeight: 500 }}>{c.name}</Typography>
                  <Typography sx={{ fontSize: 10.5, color: "text.disabled", textTransform: "capitalize" }}>{c.user_type}</Typography>
                </Box>
                <Checkbox checked={isSelected} size="small" sx={{ p: 0 }} />
              </Box>
            );
          })
        )}
      </Box>

      {selected.length > 0 && (
        <Box sx={{
          px: 2, py: 1.25, borderTop: "1px solid rgba(0,0,0,0.06)",
          display: "flex", justifyContent: "space-between", alignItems: "center",
        }}>
          <Typography sx={{ fontSize: 11.5, color: "text.secondary" }}>{selected.length} selected</Typography>
          <Button variant="contained" size="small" onClick={handleCreate} disabled={creating}
            sx={{ textTransform: "none", fontSize: 12, boxShadow: "none", borderRadius: "10px", px: 2.5, height: 32 }}>
            {creating ? <CircularProgress size={14} color="inherit" /> : "Start Chat"}
          </Button>
        </Box>
      )}
    </>
  );
};

export default ChatWidget;
